import {Injectable, OnDestroy, Type} from "@angular/core";
import {Subject} from "rxjs";
import {FUIStackedPageModalComponent} from "./stacked-page-modal/stacked-page-modal.component";
import {FUIModalsService} from "../modals/modals.service";

export class FUIStackedComponentRef {
    constructor(public component: Type<any>,
                public params: any,
                public resolveOnCancel: boolean,
                public confirmCallback: Subject<any>) {
    }
}

/**
 * Service used to stack pages in a dialog. New pages are opened in a new tab.
 * When creating the component it injects a service FUIStackedPageContextService that provides the params and functions to confirm or cancel the page.
 */
@Injectable()
export class FUIStackedPageService implements OnDestroy {

    // This is currently a singleton, but this won't work if we ever need to open a stacked page from a modal that
    // was itself opened from a stacked page.
    // If that happens, we would need to create a new modal if the current top-of-stack was not a stacked page modal component.
    stackedPageModalComponent: FUIStackedPageModalComponent;

    private creatingStackedPageModalComponent: boolean;

    private pendingOpenComponentRef: FUIStackedComponentRef[] = [];

    constructor(private modalsService: FUIModalsService) {
    }

    open(component: any, params?: any, resolveOnCancel: boolean = false): Subject<any> {
        const componentReference: FUIStackedComponentRef = new FUIStackedComponentRef(component, params, resolveOnCancel, new Subject());

        if (!this.stackedPageModalComponent) {
            if (this.creatingStackedPageModalComponent) {
                this.pendingOpenComponentRef.push(componentReference);
            } else {
                this.creatingStackedPageModalComponent = true;
                this.modalsService.$placeHolderChanged.subscribe(() => {
                    this.stackedPageModalComponent = undefined;
                });
                this.modalsService.create(FUIStackedPageModalComponent).subscribe((modal: FUIStackedPageModalComponent) => {
                    this.stackedPageModalComponent = modal;
                    this.openComponentRef(componentReference);

                    this.pendingOpenComponentRef.forEach(value => this.openComponentRef(value));

                    this.creatingStackedPageModalComponent = false;
                });
            }
        } else {
            this.openComponentRef(componentReference);
        }

        return componentReference.confirmCallback;
    }

    ngOnDestroy(): void {
        this.destroyModal();
    }

    public destroyModal(): void {
        if (this.stackedPageModalComponent) {
            this.modalsService.destroy(this.stackedPageModalComponent);
            this.stackedPageModalComponent = undefined;
        }
    }

    requestCancelCurrentView(): void {
        if (this.stackedPageModalComponent) {
            this.stackedPageModalComponent.requestCancel();
        }
    }

    isOpen(): boolean {
        if (this.stackedPageModalComponent) {
            return this.stackedPageModalComponent.views.length > 0;
        } else {
            return false;
        }
    }

    private openComponentRef(componentRef: FUIStackedComponentRef): void {
        this.modalsService.open(this.stackedPageModalComponent).subscribe(() => {
            this.stackedPageModalComponent.addComponentToView(componentRef);
        });
    }
}
